import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getDb } from "@/lib/offlineDb";
import { useRoles } from "@/hooks/useRoles";

export function useSocialFeature() {
  const { user } = useAuth();
  const { isAdmin, isCoach } = useRoles();
  const [socialEnabled, setSocialEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      // Try IDB cache first
      try {
        const db = await getDb();
        const cached = await db.get("profiles", user.id);
        if (!cancelled && cached?.features_json) {
          setSocialEnabled(cached.features_json.friends_sharing_enabled === true);
        }
      } catch { /* ignore */ }

      // Then fetch from server
      try {
        const { data } = await supabase
          .from("profiles")
          .select("features_json")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!cancelled && data) {
          const fj = (data as any).features_json;
          setSocialEnabled(fj?.friends_sharing_enabled === true);
        }
      } catch { /* offline */ }

      if (!cancelled) setLoading(false);
    };

    load();

    // Listen for profile feature updates
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.userId === user.id) {
        setSocialEnabled(detail?.features_json?.friends_sharing_enabled === true);
      }
    };
    window.addEventListener("profile-features-updated", handler as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener("profile-features-updated", handler as EventListener);
    };
  }, [user]);

  // Admins and coaches bypass the gate
  const effectiveEnabled = socialEnabled || isAdmin || isCoach;

  return { socialEnabled: effectiveEnabled, rawEnabled: socialEnabled, loading };
}
